import React, { useEffect, useRef } from 'react';
import './App.css';
import logo from './logo.svg';

// Quantum particle animation component
const QuantumParticle: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Particle class
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      opacity: number;
      phase: number;
      
      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.size = Math.random() * 5 + 1;
        this.speedX = Math.random() * 3 - 1.5;
        this.speedY = Math.random() * 3 - 1.5;
        this.color = `hsl(${Math.random() * 60 + 180}, 100%, 50%)`;
        this.opacity = Math.random() * 0.5 + 0.3;
        this.phase = Math.random() * Math.PI * 2;
      }
      
      update() {
        // Quantum effect: particles can randomly appear in different locations
        if (Math.random() < 0.005) {
          this.x = Math.random() * canvas!.width;
          this.y = Math.random() * canvas!.height;
        }
        
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Boundary check with wrapping
        if (this.x > canvas!.width) this.x = 0;
        else if (this.x < 0) this.x = canvas!.width;
        if (this.y > canvas!.height) this.y = 0;
        else if (this.y < 0) this.y = canvas!.height;
        
        // Quantum waviness effect
        this.phase += 0.05;
        this.opacity = 0.3 + Math.sin(this.phase) * 0.2;
      }
      
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();
        
        // Draw quantum "wave" effect
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 2, 0, Math.PI * 2);
        ctx.strokeStyle = this.color;
        ctx.globalAlpha = this.opacity * 0.3;
        ctx.stroke();
      }
    }
    
    // Create particles
    const particles: Particle[] = [];
    const numberOfParticles = 30;
    
    for (let i = 0; i < numberOfParticles; i++) {
      particles.push(new Particle());
    }
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="quantum-particles"></canvas>;
};

const App: React.FC = () => {
  // Top 10 featured games
  const topGames = [
    { id: 1, name: "Quantum Reels", image: "quantum-reels", rating: 4.9 },
    { id: 2, name: "Galaxy Fortune", image: "galaxy-fortune", rating: 4.8 },
    { id: 3, name: "Stellar Jackpot", image: "stellar-jackpot", rating: 4.8 },
    { id: 4, name: "Cosmic Spins", image: "cosmic-spins", rating: 4.7 },
    { id: 5, name: "Nebula Riches", image: "nebula-riches", rating: 4.7 },
    { id: 6, name: "Pulsar Pays", image: "pulsar-pays", rating: 4.6 },
    { id: 7, name: "Wormhole Wilds", image: "wormhole-wilds", rating: 4.6 },
    { id: 8, name: "Atom Multiplier", image: "atom-multiplier", rating: 4.5 },
    { id: 9, name: "Quasar Quest", image: "quasar-quest", rating: 4.5 },
    { id: 10, name: "Neutron Jackpot", image: "neutron-jackpot", rating: 4.4 }
  ];
  
  // Partner casinos
  const partnerCasinos = [
    { name: "QuBits", logo: "qubits-logo", url: "https://qubits.eventhorizongroup.ai" },
    { name: "Luxury Casino", logo: "luxury-casino", url: "#" },
    { name: "Royal Spins", logo: "royal-spins", url: "#" },
    { name: "Fortune Palace", logo: "fortune-palace", url: "#" },
    { name: "Vegas Online", logo: "vegas-online", url: "#" },
    { name: "Jackpot City", logo: "jackpot-city", url: "#" }
  ];
  
  return (
    <div className="app">
      <QuantumParticle />
      
      <header className="app-header">
        <div className="header-content">
          <div className="logo-container">
            <img src={logo} alt="Quantum Studio Logo" className="logo" />
            <h1>Quantum Studio</h1>
          </div>
          <nav className="main-nav">
            <ul>
              <li><a href="#games">Games</a></li>
              <li><a href="#top-games">Top Games</a></li>
              <li><a href="#partners">Partners</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </nav>
        </div>
      </header>
      
      <section className="hero-section">
        <div className="container">
          <h2>The Future of Online Casino Gaming</h2>
          <p>Creating premium HTML5 slot games that redefine the digital casino experience</p>
          <div className="hero-stats">
            <div className="stat">
              <h3>500+</h3>
              <p>Games Created</p>
            </div>
            <div className="stat">
              <h3>50+</h3>
              <p>Casino Partners</p>
            </div>
            <div className="stat">
              <h3>98%</h3>
              <p>Player Satisfaction</p>
            </div>
          </div>
          <div className="hero-buttons">
            <button className="cta-button primary">Explore Games</button>
            <button className="cta-button secondary">Partner With Us</button>
          </div>
        </div>
      </section>
      
      <section id="top-games" className="top-games-section">
        <div className="container">
          <h2>Our Top 10 Games</h2>
          <p>The most played and highest-rated slot games in our collection</p>
          
          <div className="top-games-grid">
            {topGames.map(game => (
              <div className="game-card" key={game.id}>
                <div className="game-image" style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('https://via.placeholder.com/300/${Math.floor(Math.random() * 16777215).toString(16)}/FFFFFF?text=${game.name.replace(' ', '+')}')`}}>
                  <div className="game-ranking">{game.id}</div>
                </div>
                <div className="game-info">
                  <h3>{game.name}</h3>
                  <div className="game-rating">
                    <span className="stars" style={{width: `${(game.rating / 5) * 100}%`}}></span>
                    <span className="rating-number">{game.rating}</span>
                  </div>
                </div>
                <button className="play-demo-btn">Play Demo</button>
              </div>
            ))}
          </div>
          
          <div className="view-more-container">
            <button className="view-more-btn">View All Games</button>
          </div>
        </div>
      </section>
      
      <section className="platform-compatibility">
        <div className="container">
          <h2>Cross-Platform Excellence</h2>
          <p>Our HTML5 games are designed to provide a seamless experience across all platforms</p>
          
          <div className="platform-grid">
            <div className="platform-card">
              <div className="platform-icon mobile-web"></div>
              <h3>Mobile Web</h3>
              <p>Optimized for all mobile browsers with responsive design and touch controls</p>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon mobile-app"></div>
              <h3>Mobile Apps</h3>
              <p>Native integration with iOS and Android applications for maximum performance</p>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon desktop"></div>
              <h3>Desktop</h3>
              <p>High-resolution graphics and immersive gameplay for large screens</p>
            </div>
          </div>
        </div>
      </section>
      
      <section id="partners" className="partners-section">
        <div className="container">
          <h2>Our Casino Partners</h2>
          <p>Trusted by the leading online casinos worldwide</p>
          
          <div className="partners-grid">
            {partnerCasinos.map((casino, index) => (
              <a href={casino.url} className="partner-card" key={index}>
                <div className="partner-logo" style={{backgroundImage: `url('https://via.placeholder.com/200/${Math.floor(Math.random() * 16777215).toString(16)}/FFFFFF?text=${casino.name.replace(' ', '+')}')`}}></div>
                <h3>{casino.name}</h3>
              </a>
            ))}
          </div>
          
          <div className="partner-cta">
            <h3>Want to feature our games on your casino?</h3>
            <button className="cta-button primary">Become a Partner</button>
          </div>
        </div>
      </section>
      
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>About Quantum Studio</h2>
              <p>Quantum Studio is a pioneering game development company specializing in creating premium HTML5 slot games for online casinos around the world.</p>
              <p>Founded in 2015, we've grown from a small team of passionate developers to an industry leader with over 500 games in our portfolio and partnerships with more than 50 online casinos.</p>
              <p>Our mission is to push the boundaries of what's possible in online casino gaming through innovative mechanics, stunning visuals, and engaging player experiences.</p>
              <p>Quantum Studio is a proud subsidiary of <a href="https://eventhorizongroup.ai">Event Horizon Group</a>, a global leader in digital entertainment.</p>
            </div>
            <div className="about-image">
              <img src={`${process.env.PUBLIC_URL}/images/studio-team.jpg`} alt="Quantum Studio Team" />
            </div>
          </div>
        </div>
      </section>
      
      <section id="contact" className="contact-section">
        <div className="container">
          <h2>Get In Touch</h2>
          <p>Interested in our games or want to discuss a partnership? Contact us today.</p>
          
          <div className="contact-grid">
            <div className="contact-form">
              <form>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input type="text" id="name" placeholder="Your name" />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" placeholder="Your email" />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select id="subject">
                    <option value="partnership">Casino Partnership</option>
                    <option value="support">Game Support</option>
                    <option value="career">Career Opportunities</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" placeholder="Your message" rows={5}></textarea>
                </div>
                
                <button type="submit" className="cta-button primary">Send Message</button>
              </form>
            </div>
            
            <div className="contact-info">
              <div className="info-item">
                <h3>Email</h3>
                <p>contact@quantumstudio.example.com</p>
              </div>
              
              <div className="info-item">
                <h3>Phone</h3>
                <p>+1 (555) 123-4567</p>
              </div>
              
              <div className="info-item">
                <h3>Address</h3>
                <p>100 Innovation Drive<br />Silicon Valley, CA 94024</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <footer className="app-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src={logo} alt="Quantum Studio Logo" className="small-logo" />
              <h3>Quantum Studio</h3>
            </div>
            
            <div className="footer-links">
              <div className="footer-links-column">
                <h4>Games</h4>
                <ul>
                  <li><a href="#games">All Games</a></li>
                  <li><a href="#top-games">Top Games</a></li>
                  <li><a href="#new-releases">New Releases</a></li>
                </ul>
              </div>
              
              <div className="footer-links-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="#about">About Us</a></li>
                  <li><a href="#team">Our Team</a></li>
                  <li><a href="#careers">Careers</a></li>
                </ul>
              </div>
              
              <div className="footer-links-column">
                <h4>Resources</h4>
                <ul>
                  <li><a href="#docs">Developer Docs</a></li>
                  <li><a href="#api">API Reference</a></li>
                  <li><a href="#blog">Blog</a></li>
                </ul>
              </div>
              
              <div className="footer-links-column">
                <h4>Partners</h4>
                <ul>
                  <li><a href="https://qubits.eventhorizongroup.ai">QuBits Casino</a></li>
                  <li><a href="https://eventhorizongroup.ai">Event Horizon Group</a></li>
                  <li><a href="#partnership">Partnership Program</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Quantum Studio. All rights reserved. A subsidiary of <a href="https://eventhorizongroup.ai">Event Horizon Group</a>.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
// Quantum logo SVG component
const QuantumLogo: React.FC<{className?: string}> = ({className}) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={className}>
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{stopColor:'#6610f2',stopOpacity:1}} />
        <stop offset="100%" style={{stopColor:'#0dcaf0',stopOpacity:1}} />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="45" fill="url(#grad1)" />
    <circle cx="50" cy="50" r="20" fill="none" stroke="white" strokeWidth="2" opacity="0.7">
      <animate attributeName="r" values="20;35;20" dur="3s" repeatCount="indefinite" />
    </circle>
    <circle cx="50" cy="50" r="30" fill="none" stroke="white" strokeWidth="1" opacity="0.5">
      <animate attributeName="r" values="30;20;30" dur="2s" repeatCount="indefinite" />
    </circle>
  </svg>
); 